import DrawerModal from 'components/DrawerModal';
import { Hero as HeroSection } from 'components/Hero';
import Layout from 'components/Layout';
import { ProductCardGrid } from 'components/ProductCardGrid';
import { SEO } from 'components/SEO';
import { graphql } from 'gatsby';
import everlywellPwn from 'images/everlywellPwn.png';
import React, { lazy, useEffect, useState } from 'react';
import track from 'utils/analytics/track';
import { ANALYTICS } from 'utils/constants/analytics';
import { IContentWithImage } from 'utils/types';

const OrganizationCardsSection = lazy(
  () => import('containers/Labs/OrganizationCardsSection'),
);
const StatsWithImages = lazy(() => import('components/StatsWithImages'));
const MembersSatisfaction = lazy(
  () => import('containers/HealthPlansPage/MembersSatisfaction'),
);
const HowItWorksSection = lazy(
  () => import('components/EverlyCarePage/HowItWorks'),
);
const StepCardListSection = lazy(() => import('components/StepCardList'));

type Props = {
  data: {
    contentfulPage001: {
      hero: {
        eyebrow: string;
        eyebrowColor: string;
        headline: string;
        backgroundColor: string;
        desktopImage: {
          url: string;
          title: string;
        };
        cta: {
          text: string;
          url: string;
        };
      };
      sections: {
        componentName: string;
        headline: string;
        disclaimer?: {
          disclaimer: string;
        };
        description: {
          description: string;
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
        slug: string;
        title?: string;
        listOfContentWithImages?: IContentWithImage[];
        container?: any;
      }[];
    };
  };
};

const components: Record<string, React.ComponentType<any>> = {
  HeroSection,
  MembersSatisfaction,
  StatsWithImages,
  HowItWorksSection,
  StepCardList: StepCardListSection,
  OrganizationCardsSection,
};

const isProductCardGrid = (name: string): boolean => name === 'ProductCardGrid';
const isStepCardList = (name: string): boolean => name === 'StepCardList';

export const Head = () => <SEO />;

const LabsPage = React.memo<Props>(({ data }) => {
  const { hero, sections } = data.contentfulPage001;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
      data: {
        label: 'EHS Labs page',
      },
    });
  }, []);

  useEffect(() => {
    if (/www.pwnhealth.com/.test(document.referrer)) {
      setIsModalOpen(true);
    }
  }, []);

  return (
    <Layout>
      <DrawerModal
        modalOpen={isModalOpen}
        modalOpenCallback={(isOpen: boolean) => setIsModalOpen(isOpen)}
        screenerData={{
          callToActionText: 'Contact us',
          secondaryCallToActionText: 'Close',
          callToActionUrl: 'mailto:gc@pwnhealth.com',
          bodyImg: everlywellPwn,
          title: 'Everlywell + PWNHealth',
          description: `
            <div data-component="ContentBox">
              <p>PWNHealth, an Everlywell company, works with a trusted network of independent healthcare professionals — including doctors, nurse practitioners, genetic counselors, nurses, and medical assistants — to provide clinical support for your test.</p>
              <p>These licensed professionals review and oversee your lab testing to help ensure it’s handled safely and appropriately. Please note: PWNHealth is separate from the lab that processes your test and from Everlywell, the company that provides your test kit.</p>
            </div>
          `,
        }}
      />

      <HeroSection {...hero} />

      {sections.map(
        (
          section: Props['data']['contentfulPage001']['sections'][number],
          i: number,
        ) => {
          const Component =
            components[section.componentName as keyof typeof components];

          if (!Component) return null;

          if (isProductCardGrid(section.componentName)) {
            return (
              <ProductCardGrid
                key={`${section.componentName}-${i}`}
                {...section}
                title={section.title ?? ''}
                cards={section.container}
                sectionHeader={{
                  ...section,
                  eyebrow: section.title || '',
                  title: section.headline || '',
                  description:
                    section.description?.childMarkdownRemark?.rawMarkdownBody,
                }}
                showOverlay={false}
                cardsPerRow={3}
              />
            );
          }

          if (isStepCardList(section.componentName)) {
            return (
              <StepCardListSection
                key={`${section.componentName}-${i}`}
                eyebrow={section.title || ''}
                heading={section.headline}
                listOfContentWithImages={section.listOfContentWithImages ?? []}
                description={section.description}
              />
            );
          }

          return (
            <Component key={`${section.componentName}-${i}`} {...section} />
          );
        },
      )}
    </Layout>
  );
});

export default LabsPage;

export const data = graphql`
  query Query {
    contentfulPage001(displayName: { eq: "Labs page | EHS" }) {
      slug
      hero {
        ... on ContentfulHeroSection {
          eyebrow
          eyebrowColor
          headline
          description {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          backgroundColor
          desktopImage {
            url
            title
          }
          cta {
            text
            url
          }
        }
      }
      sections {
        headline
        markdownField {
          childMarkdownRemark {
            html
          }
        }
        title
        subHeadline
        componentName
        slug
        backgroundColor
        description {
          description
        }
        disclaimer {
          childMarkdownRemark {
            html
            rawMarkdownBody
          }
        }
        sectionImage {
          title
          url
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
        cta {
          text
          url
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            backgroundColor
            heading
            description {
              description
              childMarkdownRemark {
                rawMarkdownBody
                html
              }
            }
            image {
              title
              url
            }
            cta {
              text
              url
            }
          }
        }
      }
    }
  }
`;
